import {
  CHANNEL_HIGH_WATER_MARK_BYTES,
  CHANNEL_LOW_WATER_MARK_BYTES,
  FALLBACK_MESSAGE_BYTES,
  MAX_MESSAGE_BYTES,
  READ_BLOCK_BYTES,
} from '../../shared/constants.js';
import type { FileMeta, ReceiverControl, SenderControl } from '../../shared/transfer.js';
import { chunkRange, countChunks, parseReceiverControl } from '../../shared/transfer.js';
import { PeerConnectionError, TransferRejectedError } from './errors.js';

export type SendProgress = Readonly<{
  /** Bytes handed to the data channel. */
  sentBytes: number;
  /** Bytes the receiver confirmed it has stored. */
  ackedBytes: number;
  totalBytes: number;
}>;

export type SendCallbacks = Readonly<{
  /** The receiver chose a destination and asked for the bytes. */
  onAccepted: () => void;
  onProgress: (progress: SendProgress) => void;
}>;

export type SendOptions = Readonly<{
  messageBytes: number;
  readBlockBytes: number;
}>;

export const fileMetaOf = (file: File): FileMeta => ({ name: file.name, size: file.size, mimeType: file.type });

/**
 * Picks the message size from the SCTP maximum the remote peer advertised.
 * Unknown (undefined or 0) means "assume the conservative value every browser accepts".
 */
export const chooseMessageBytes = (advertisedMaxMessageBytes: number | undefined): number => {
  if (advertisedMaxMessageBytes === undefined || !Number.isFinite(advertisedMaxMessageBytes) || advertisedMaxMessageBytes <= 0) {
    return FALLBACK_MESSAGE_BYTES;
  }
  return Math.min(MAX_MESSAGE_BYTES, Math.floor(advertisedMaxMessageBytes));
};

export const sendOptionsFor = (pc: Pick<RTCPeerConnection, 'sctp'>): SendOptions => ({
  messageBytes: chooseMessageBytes(pc.sctp?.maxMessageSize),
  readBlockBytes: READ_BLOCK_BYTES,
});

const sendControl = (channel: RTCDataChannel, message: SenderControl): void => channel.send(JSON.stringify(message));

const waitForBufferDrain = (channel: RTCDataChannel): Promise<void> =>
  new Promise((resolve, reject) => {
    if (channel.bufferedAmount <= CHANNEL_LOW_WATER_MARK_BYTES) {
      resolve();
      return;
    }
    const onLow = (): void => {
      channel.removeEventListener('close', onClose);
      resolve();
    };
    const onClose = (): void => {
      channel.removeEventListener('bufferedamountlow', onLow);
      reject(new PeerConnectionError('Data channel closed while sending'));
    };
    channel.addEventListener('bufferedamountlow', onLow, { once: true });
    channel.addEventListener('close', onClose, { once: true });
  });

type ReceiverEvents = Readonly<{
  accepted: Promise<void>;
  completed: Promise<void>;
}>;

/** Subscribes to receiver control messages and exposes the two milestones as promises. */
const listenToReceiver = (channel: RTCDataChannel, onAck: (bytes: number) => void): ReceiverEvents => {
  let resolveAccepted: () => void = () => undefined;
  let resolveCompleted: () => void = () => undefined;
  let fail: (error: Error) => void = () => undefined;

  const accepted = new Promise<void>((resolve, reject) => {
    resolveAccepted = resolve;
    fail = reject;
  });
  const completed = new Promise<void>((resolve, reject) => {
    resolveCompleted = resolve;
    const previousFail = fail;
    fail = (error) => {
      previousFail(error);
      reject(error);
    };
  });

  const handle = (message: ReceiverControl): void => {
    switch (message.type) {
      case 'accept':
        resolveAccepted();
        break;
      case 'reject':
        fail(new TransferRejectedError(message.reason));
        break;
      case 'progress':
        onAck(message.receivedBytes);
        break;
      case 'complete':
        resolveCompleted();
        break;
    }
  };

  channel.addEventListener('message', (event: MessageEvent<unknown>) => {
    if (typeof event.data === 'string') {
      handle(parseReceiverControl(event.data));
    }
  });
  channel.addEventListener('close', () => fail(new PeerConnectionError('Data channel closed by the receiver')));

  return { accepted, completed };
};

/** Sends one block that is already in memory as a series of messages, respecting channel backpressure. */
const sendBlock = async (
  channel: RTCDataChannel,
  block: ArrayBuffer,
  messageBytes: number,
  onMessageSent: (bytes: number) => void,
): Promise<void> => {
  const messages = countChunks(block.byteLength, messageBytes);
  for (let index = 0; index < messages; index += 1) {
    const range = chunkRange(index, block.byteLength, messageBytes);
    if (channel.bufferedAmount > CHANNEL_HIGH_WATER_MARK_BYTES) {
      await waitForBufferDrain(channel);
    }
    channel.send(new Uint8Array(block, range.start, range.end - range.start));
    onMessageSent(range.end - range.start);
  }
};

export const sendFile = async (
  channel: RTCDataChannel,
  file: File,
  options: SendOptions,
  callbacks: SendCallbacks,
): Promise<void> => {
  channel.bufferedAmountLowThreshold = CHANNEL_LOW_WATER_MARK_BYTES;
  let sentBytes = 0;
  let ackedBytes = 0;
  const report = (): void => callbacks.onProgress({ sentBytes, ackedBytes, totalBytes: file.size });

  const receiver = listenToReceiver(channel, (bytes) => {
    ackedBytes = bytes;
    report();
  });

  sendControl(channel, { type: 'meta', file: fileMetaOf(file) });
  await receiver.accepted;
  callbacks.onAccepted();

  const blocks = countChunks(file.size, options.readBlockBytes);
  for (let index = 0; index < blocks; index += 1) {
    const range = chunkRange(index, file.size, options.readBlockBytes);
    const block = await file.slice(range.start, range.end).arrayBuffer();
    await sendBlock(channel, block, options.messageBytes, (bytes) => {
      sentBytes += bytes;
      report();
    });
  }

  sendControl(channel, { type: 'done' });
  await receiver.completed;
  ackedBytes = file.size;
  report();
};
